import type { ReactNode } from 'react';

/**
 * Shared UI atoms for the Compensation module (โครงสร้างค่าตอบแทน).
 * Keeps the tab files consistent with the PayPilot design system (semantic
 * tokens, no native dialogs, flat). Mirrors OrgStructure.tsx styling.
 */

export const inputCls =
  'w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--brand-600)] focus:ring-2 focus:ring-[var(--brand-100)]';
export const tabActive =
  'whitespace-nowrap border-b-2 border-[var(--brand-600)] px-4 py-2 text-sm font-semibold text-[var(--brand-700)]';
export const tabIdle =
  'whitespace-nowrap px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text)]';
export const btnPrimary =
  'rounded-lg bg-[var(--brand-600)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-700)] disabled:opacity-50';
export const btnGhost = 'rounded-lg border border-[var(--border)] px-4 py-2 text-sm';

export function thb(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return '฿' + Math.round(n).toLocaleString('en-US');
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-[var(--text-muted)]">{hint}</p> : null}
    </div>
  );
}

export function Select({
  value, onChange, options, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { id: string; name: string }[];
  placeholder: string;
}) {
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
}

export function EmptyState({ text }: { text: string }) {
  return (
    <p className="rounded-lg border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-muted)]">
      {text}
    </p>
  );
}

export function TableShell({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
      <table className="w-full text-sm">
        <thead className="bg-[var(--surface-2)] text-left text-xs text-[var(--text-muted)]">{head}</thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function SectionHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold text-[var(--text)]">{title}</h2>
        {subtitle ? <p className="text-sm text-[var(--text-muted)]">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}
